/**
 * `<PageHeader>` — page identity + context + actions row.
 *
 * Foundations spec: design/design-system-foundations.md §4.4.
 * Authoritative slot contract: design/post-walkthrough/shell-spec.md §4.
 * Canon anchor: UI_RECREATION_PROMPT.md §5 (Header bar, 60px single-row).
 *
 * Composition (Batch 9 — canon strip-down, single-row at lg+):
 *
 *   lg+ (≥1024px) — SINGLE row, matches prototype #hdr:
 *     [ title text-base font-semibold ] [ hdr-sep border-l 16px tall ]
 *     [ contextSelector ] [ contextStats flex-1, gap-x-[18px] ]
 *     [ action cluster flex-shrink-0 whitespace-nowrap ]
 *
 *   <lg (<1024px) — two-row graceful degradation (preserves the 30h.1.3
 *   clipping fix at narrow widths):
 *     Row 1: [ title + meta ] … [ secondary… primary ]
 *     Row 2: [ contextSelector | divider | contextStats ]
 *
 * Title sizing (prototype §2.2): `text-base` (15px) weight 600 at lg+, NOT
 * `text-2xl`. At <lg the title bumps to a hair larger for readability but
 * still stays subordinate to the prototype hierarchy.
 *
 * Action buttons that change label width during in-flight states (e.g.
 * "Re-rank" → "Re-ranking…") should use the Button component's `loading`
 * prop, which collapses to constant-width via icon-only treatment. The
 * prototype's "⟳ Re-rank" stays-same-width during ranking.
 *
 * Why the lg+/<lg split: the prior single-row collapse (30h.1.3) broke at
 * 1024 + 1280 when an action label grew long. The Phase 2.2.1 two-row
 * refactor over-corrected — chrome height jumped to ~110-130px, drifting
 * from the prototype's 60px. Batch 9 reverts to single-row at lg+ where
 * there's room, keeps two-row at <lg as defensive degradation.
 *
 * AppShell consumes this header inside a `min-h-header-h` band so the
 * reserved chrome height grows naturally when row 2 renders. The portal
 * contract (PageHeaderSlotProvider, 30g) is unchanged.
 */
import type { JSX, ReactNode } from "react";

interface PageHeaderProps {
  /**
   * Page title rendered in the header band. Omit (or pass `undefined`) to
   * suppress the title entirely — the contextSelector / contextStats /
   * action slots remain and the band still lays out correctly.
   */
  title?: string;
  contextSelector?: ReactNode;
  contextStats?: ReactNode;
  primaryAction?: ReactNode;
  /**
   * Array of secondary action nodes rendered left of `primaryAction`.
   * Replaces the singular `secondaryAction` prop (Phase 1 rename).
   * Consumers that previously passed a single node now wrap it in an array.
   */
  secondaryActions?: ReactNode[];
  /** Rare italic small meta text rendered alongside the title. */
  meta?: ReactNode;
}

// 16px-tall vertical divider, prototype `.hdr-sep` analog.
const HDR_SEP = "hidden h-4 border-l border-border-default lg:block" as const;
// Two-row divider — Row 2 only (kept for <lg fallback parity with old behavior).
const ROW2_DIVIDER = "h-6 border-l border-border-default" as const;

export function PageHeader({
  title,
  contextSelector,
  contextStats,
  primaryAction,
  secondaryActions,
  meta,
}: PageHeaderProps): JSX.Element {
  const hasActions =
    primaryAction !== undefined ||
    (secondaryActions !== undefined && secondaryActions.length > 0);
  const hasSelector = contextSelector !== undefined && contextSelector !== null;
  const hasStats = contextStats !== undefined && contextStats !== null;
  const hasContextRow = hasSelector || hasStats;

  return (
    <header
      className="px-content-px py-2 lg:py-3"
      data-testid="page-header"
    >
      {/* ── lg+ single-row (canon — prototype #hdr 60px tall) ─────────── */}
      <div className="hidden items-center gap-3 lg:flex">
        {/* Title cluster — fixed width, no flex-1 grab. Omitted when title
            is undefined (Dashboard top-bar cleanup 2026-06-16). */}
        {title !== undefined ? (
          <h1 className="whitespace-nowrap text-base font-semibold tracking-tight text-text-emphasis">
            {title}
          </h1>
        ) : null}
        {meta ? (
          <span className="whitespace-nowrap text-sm text-text-secondary">
            {meta}
          </span>
        ) : null}
        {hasSelector ? (
          <>
            <span aria-hidden="true" className={HDR_SEP} />
            <div className="flex items-center">{contextSelector}</div>
          </>
        ) : null}
        {hasStats ? (
          <div
            data-testid="page-header-stats-inline"
            className="flex flex-1 items-center gap-x-[18px]"
          >
            {contextStats}
          </div>
        ) : (
          <div className="flex-1" aria-hidden="true" />
        )}
        {hasActions ? (
          <div className="flex flex-shrink-0 items-center gap-2 whitespace-nowrap">
            {secondaryActions?.map((action, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: positional slot — consumers own ordering
              <span key={i} className="whitespace-nowrap">
                {action}
              </span>
            ))}
            {primaryAction ? (
              <span className="whitespace-nowrap">{primaryAction}</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* ── <lg two-row fallback (defensive — preserves 30h.1.3 fix) ─── */}
      <div className="flex flex-col gap-3 lg:hidden">
        {/* Row 1 — title left, actions right. */}
        <div className="flex items-center gap-3">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {title !== undefined ? (
              <h1 className="truncate text-base font-semibold tracking-tight text-text-emphasis">
                {title}
              </h1>
            ) : null}
            {meta ? (
              <span className="truncate text-sm text-text-secondary">
                {meta}
              </span>
            ) : null}
          </div>
          {hasActions ? (
            <div className="flex flex-shrink-0 items-center gap-2 whitespace-nowrap">
              {secondaryActions?.map((action, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: positional slot — consumers own ordering
                <span key={i} className="whitespace-nowrap">
                  {action}
                </span>
              ))}
              {primaryAction ? (
                <span className="whitespace-nowrap">{primaryAction}</span>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Row 2 — contextSelector + divider + contextStats (shell-spec §4.2).
            Renders only when at least one of the two slots is present. */}
        {hasContextRow ? (
          <div
            data-testid="page-header-row-2"
            className="flex flex-wrap items-center gap-x-4 gap-y-3"
          >
            {hasSelector ? (
              <div className="flex items-center">{contextSelector}</div>
            ) : null}
            {hasSelector && hasStats ? (
              <span aria-hidden="true" className={ROW2_DIVIDER} />
            ) : null}
            {hasStats ? (
              <div className="flex flex-1 items-center">{contextStats}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
